import { useEffect, useRef, useState } from "react";
import { useFliptBoolean } from "@flipt-io/flipt-client-react";
import { Expand } from "lucide-react";
import { Highlight, themes } from "prism-react-renderer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { ArtifactToolbarActions } from "~/components/assistant/messages/ArtifactToolbarActions";
import { DataTable } from "~/components/assistant/messages/DataTable";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { FullscreenArtifactModal } from "~/components/assistant/messages/FullscreenArtifactModal";
import { LineageTab } from "~/components/assistant/messages/LineageTab";
import { DownloadDialog } from "~/components/assistant/messages/download/DownloadDialog";
import { ExportDialog } from "~/components/assistant/messages/export/ExportDialog";
import { Dataframe } from "~/components/assistant/types";
import useUser from "~/contexts/user";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";

interface DataframeMessageProps {
  dataframe: Dataframe;
  debugMode?: boolean;
}

type TabType = "table" | "code" | "lineage" | "details";

interface Tab {
  id: TabType;
  label: string;
}
const MIN_INLINE_ARTIFACT_WIDTH = 400;

// DataframeMessage component with tabbed interface
export function DataframeMessage({ dataframe, debugMode = false }: DataframeMessageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("table");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [isCompactArtifactView, setIsCompactArtifactView] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);
  const { conversationId, contextId, sendPrompt } = useAssistantContext();
  const user = useUser();
  const exportToWorkbenchEnabled = useFliptBoolean("ai-export-artifact-to-rwb", false, user?.email);

  // Load Python language support for syntax highlighting
  const { isLoading, Prism: PrismInstance } = usePrismLanguages(["python"]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    trackEvent("analysis_agent_artifact_tab_viewed", {
      dataset_id: contextId || "Unknown",
      conversation_id: conversationId || undefined,
      artifact_id: dataframe.artifact_id || undefined,
      artifact_type: "dataframe",
      context: "dataset",
      tab,
    });
  };

  const tabs: Tab[] = [{ id: "table", label: "Table" }];
  if (dataframe?.code) {
    tabs.push({ id: "code", label: "Code" });
  }

  if (dataframe?.artifact_id) {
    tabs.push({ id: "lineage", label: "Lineage" });
  }

  if (debugMode) {
    tabs.push({ id: "details", label: "Details" });
  }

  useEffect(() => {
    const element = messageRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) {
        return;
      }
      setIsCompactArtifactView(entry.contentRect.width <= MIN_INLINE_ARTIFACT_WIDTH);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleOpenFullscreen = (source: "toolbar" | "compact-placeholder") => {
    setIsFullscreen(true);
    trackEvent("analysis_agent_artifact_fullscreen_opened", {
      dataset_id: contextId || "Unknown",
      conversation_id: conversationId || undefined,
      artifact_id: dataframe.artifact_id || undefined,
      artifact_type: "dataframe",
      source,
    });
  };

  const renderTabButtons = () =>
    tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => handleTabChange(tab.id)}
        className={cn(
          "px-3 py-2 text-xs font-medium border-b-2 transition-colors select-none cursor-pointer",
          "max-w-20 min-w-0 overflow-hidden whitespace-nowrap text-ellipsis break-normal",
          activeTab === tab.id
            ? "border-gray-900 text-gray-900"
            : "border-transparent text-gray-600 hover:text-gray-900"
        )}
      >
        {tab.label}
      </button>
    ));

  const renderTableContent = () => {
    if (activeTab !== "table") return null;
    return (
      <div>
        <DataTable
          data={dataframe.content.data}
          totalDataRows={dataframe.content.totalRows}
          displayedRows={dataframe.content.displayedRows}
        />
        {dataframe?.summary && (
          <div className="mt-3 text-xs text-gray-600">{dataframe.summary}</div>
        )}
      </div>
    );
  };

  const renderCodeContent = () => {
    if (activeTab !== "code" || !dataframe?.code) return null;
    return (
      <>
        {/* Code Header with Copy Button */}
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-medium text-gray-700">Python Code</h4>
          <CopyButton text={dataframe.code} variant="light" size="sm" />
        </div>
        <div className="h-full overflow-hidden rounded-sm">
          {!isLoading && (
            <Highlight
              theme={themes.dracula}
              code={dataframe.code}
              language="python"
              prism={PrismInstance as any}
            >
              {({ style, tokens, getLineProps, getTokenProps, className }) => (
                <pre
                  className={cn(
                    "not-prose h-full p-4 overflow-x-auto text-xs font-mono leading-relaxed",
                    className
                  )}
                  style={style}
                >
                  {tokens.map((line, i) => (
                    <div
                      {...getLineProps({ line, key: i })}
                      key={`line-${i}`}
                      style={{ minHeight: "1.5em" }}
                    >
                      {line.map((token, key) => (
                        <span {...getTokenProps({ token, key })} key={`token-${key}`} />
                      ))}
                    </div>
                  ))}
                </pre>
              )}
            </Highlight>
          )}
          {isLoading && (
            <div className="h-full p-4 bg-gray-800 text-gray-300 text-sm font-mono leading-relaxed">
              Loading syntax highlighting...
            </div>
          )}
        </div>
      </>
    );
  };

  const renderLineageContent = () => {
    if (activeTab !== "lineage" || !dataframe?.artifact_id) return null;
    return <LineageTab artifactId={dataframe.artifact_id} />;
  };

  return (
    <>
      <div ref={messageRef} className="bg-white border border-gray-200 rounded-lg p-4 pt-1 w-full">
        {/* Tabs */}
        <div className="flex items-center border-b border-gray-200 mb-4 overflow-hidden">
          {renderTabButtons()}
          <ArtifactToolbarActions
            featureFlagEnabled={exportToWorkbenchEnabled}
            onDownload={
              dataframe.artifact_id
                ? () => {
                    setShowDownloadDialog(true);
                    trackEvent("analysis_agent_artifact_download_requested", {
                      dataset_id: contextId || "Unknown",
                      conversation_id: conversationId || undefined,
                      artifact_id: dataframe.artifact_id || undefined,
                      artifact_type: "dataframe",
                    });
                  }
                : undefined
            }
            showGenerateFile={!!(sendPrompt && dataframe.artifact_name)}
            onGenerateFile={() => {
              sendPrompt?.(
                `Create a CSV file from the dataframe artifact "${dataframe.artifact_name}"`
              );
              trackEvent("analysis_agent_artifact_download_requested", {
                dataset_id: contextId || "Unknown",
                conversation_id: conversationId || undefined,
                artifact_id: dataframe.artifact_id || undefined,
                artifact_type: "dataframe",
              });
            }}
            onExport={() => {
              setShowExportDialog(true);
              trackEvent("analysis_agent_artifact_export_opened", {
                dataset_id: contextId || "Unknown",
                conversation_id: conversationId || undefined,
                artifact_id: dataframe.artifact_id || undefined,
                artifact_type: "dataframe",
              });
            }}
            onFullscreen={() => handleOpenFullscreen("toolbar")}
          />
        </div>

        {/* Tab Content */}
        <div className="overflow-x-auto">
          {activeTab === "table" && isCompactArtifactView ? (
            <div className="flex min-h-48 items-center justify-center rounded-md px-4 py-5">
              <div className="flex max-w-xs flex-col items-center gap-3 text-center">
                <p className="text-xs text-gray-600">This table is easier to read in fullscreen.</p>
                <button
                  type="button"
                  onClick={() => handleOpenFullscreen("compact-placeholder")}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-800 transition-colors hover:bg-gray-100 cursor-pointer"
                >
                  <Expand className="size-3.5" />
                  View this table
                </button>
              </div>
            </div>
          ) : (
            renderTableContent()
          )}
          {renderCodeContent()}
          {renderLineageContent()}
          {activeTab === "details" && debugMode && (
            <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200 text-gray-800">
              <DetailField label="Dataframe Data" content={dataframe} />
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Modal */}
      <FullscreenArtifactModal
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        title="Data Table"
      >
        {/* Tabs (no fullscreen button in modal) */}
        <div className="flex border-b border-gray-200 mb-4 shrink-0">{renderTabButtons()}</div>

        {/* Content fills remaining space */}
        <div className="flex-1 min-h-0 overflow-auto">
          {renderTableContent()}
          {renderCodeContent()}
          {renderLineageContent()}
          {activeTab === "details" && debugMode && (
            <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200 text-gray-800">
              <DetailField label="Dataframe Data" content={dataframe} />
            </div>
          )}
        </div>
      </FullscreenArtifactModal>

      {/* Export Dialog */}
      <ExportDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        artifact={dataframe}
        artifactType="dataframe"
        conversationId={conversationId || ""}
      />

      {/* Download Dialog */}
      <DownloadDialog
        open={showDownloadDialog}
        onClose={() => setShowDownloadDialog(false)}
        artifact={dataframe}
        artifactType="dataframe"
      />
    </>
  );
}
